import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';

import { formatSiret } from '@e-socna/third-parties';

import { formatDate } from '../../utils/format-date.util';

import { isActiveAdministrativeStatus } from './utils/is-active-administrative-status.util';
import { resolveNafLabel } from './utils/resolve-naf-label.util';
import type { OrganizationSearchEstablishment } from './types/organization-search-result.type';

interface Props {
  readonly establishment: Readonly<OrganizationSearchEstablishment>;
  readonly isAlreadyCreated: boolean;
  readonly isHeadOffice: boolean;
  readonly nafDesignationByCode: ReadonlyMap<string, string>;
}

const resolveActivityPeriodLabel = (
  establishment: Readonly<OrganizationSearchEstablishment>,
) => {
  if (!establishment.date_debut_activite) {
    return undefined;
  }

  return isActiveAdministrativeStatus(establishment.etat_administratif)
    ? `Active depuis le ${formatDate(establishment.date_debut_activite)}`
    : `Active du ${formatDate(establishment.date_debut_activite)} au ${
        establishment.date_fermeture
          ? formatDate(establishment.date_fermeture)
          : '?'
      }`;
};

export const EstablishmentItem = ({
  establishment,
  isAlreadyCreated,
  isHeadOffice,
  nafDesignationByCode,
}: Readonly<Props>) => {
  const activityPeriodLabel = resolveActivityPeriodLabel(establishment);
  const activityLabel = resolveNafLabel(
    establishment.activite_principale_naf25,
    establishment.activite_principale,
    nafDesignationByCode,
  );

  return (
    <Stack spacing={0.25}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
          {isHeadOffice ? (
            <BusinessIcon color="action" fontSize="small" />
          ) : (
            <StoreIcon color="action" fontSize="small" />
          )}
          <Typography color="text.secondary" variant="subtitle2">
            {isHeadOffice ? 'Siège' : 'Établissement'}
          </Typography>
        </Stack>
        {isAlreadyCreated ? (
          <Chip
            color="success"
            label="Déjà créé"
            size="small"
            sx={{ flexShrink: 0 }}
          />
        ) : null}
      </Stack>
      <Typography color="text.secondary" variant="body2">
        SIRET {formatSiret(establishment.siret)}
      </Typography>
      {establishment.adresse ? (
        <Typography color="text.secondary" variant="body2">
          {establishment.adresse}
        </Typography>
      ) : null}
      {activityPeriodLabel ? (
        <Typography color="text.secondary" variant="body2">
          {activityPeriodLabel}
        </Typography>
      ) : null}
      {activityLabel ? (
        <Typography color="text.secondary" variant="body2">
          Activité : {activityLabel}
        </Typography>
      ) : null}
      {establishment.tranche_effectif_salarie ? (
        <Typography color="text.secondary" variant="body2">
          Effectif : {establishment.tranche_effectif_salarie}
        </Typography>
      ) : null}
      {establishment.date_creation ? (
        <Typography color="text.secondary" variant="body2">
          Date de création : {formatDate(establishment.date_creation)}
        </Typography>
      ) : null}
    </Stack>
  );
};
